import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  FileArchive,
  Loader2,
  RefreshCw,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ExportRequestCreateDto,
  ExportRequestDto,
  ExportService,
} from "../../../../services/ExportService";

interface DataExportManagerProps {
  className?: string;
}

export default function DataExportManager({
  className = "",
}: DataExportManagerProps) {
  const [exports, setExports] = useState<ExportRequestDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Ref pour éviter les dépendances dans useEffect
  const exportsRef = useRef<ExportRequestDto[]>([]);

  // Formulaire de création
  const [newFormat, setNewFormat] = useState<"ZIP" | "JSON">("ZIP");
  const [newPeriod, setNewPeriod] = useState<
    "ALL" | "LAST_YEAR" | "LAST_MONTH"
  >("ALL");

  const { toast } = useToast();

  // Mettre à jour la ref quand exports change
  useEffect(() => {
    exportsRef.current = exports;
  }, [exports]);

  // Charger les exports
  const loadExports = useCallback(async () => {
    try {
      const userExports = await ExportService.getUserExports();
      setExports(userExports);
    } catch (error: any) {
      console.error("Erreur lors du chargement des exports:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des exports",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Créer un nouvel export
  const createExport = async () => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const request: ExportRequestCreateDto = {
        format: newFormat,
        period: newPeriod,
      };

      await ExportService.createExport(request);

      toast({
        title: "Export demandé",
        description:
          "Votre demande d'export a été créée. Le traitement peut prendre quelques minutes.",
      });

      setIsDialogOpen(false);

      // Recharger la liste
      await loadExports();
    } catch (error: any) {
      console.error("Erreur lors de la création de l'export:", error);

      let errorMessage = "Impossible de créer l'export";
      if (error.cause?.includes("24h")) {
        errorMessage =
          "Vous pouvez seulement demander un export toutes les 24 heures";
      }

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Télécharger un export
  const downloadExport = async (exportData: ExportRequestDto) => {
    try {
      await ExportService.downloadExport(
        exportData.id,
        exportData.fileName || `export-${exportData.id}.zip`
      );

      toast({
        title: "Téléchargement démarré",
        description: "Le fichier va être téléchargé dans quelques instants.",
      });
    } catch (error: any) {
      console.error("Erreur lors du téléchargement:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger l'export",
        variant: "destructive",
      });
    }
  };

  // Polling pour mettre à jour les statuts
  useEffect(() => {
    // Charger les exports au démarrage
    loadExports();

    // Polling toutes les 5 secondes si il y a des exports en cours
    const interval = setInterval(() => {
      const hasRunningExports = exportsRef.current.some(
        (exp) => exp.status === "PENDING" || exp.status === "RUNNING"
      );

      if (hasRunningExports) {
        loadExports();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [loadExports]);

  // Icône et couleur selon le statut
  const getStatusIcon = (status: ExportRequestDto["status"]) => {
    switch (status) {
      case "PENDING":
        return <Clock className="h-4 w-4" />;
      case "RUNNING":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "DONE":
        return <CheckCircle className="h-4 w-4" />;
      case "FAILED":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusVariant = (status: ExportRequestDto["status"]) => {
    switch (status) {
      case "PENDING":
        return "secondary";
      case "RUNNING":
        return "default";
      case "DONE":
        return "default"; // Utilise la couleur par défaut qui est généralement verte
      case "FAILED":
        return "destructive";
      default:
        return "secondary";
    }
  };

  // Vérifier si on peut créer un nouvel export (limite 24h)
  const canCreateNewExport = () => {
    const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
    // Ne compter que les exports DONE dans les dernières 24h (comme le backend)
    return !exports.some(
      (exp) => exp.status === "DONE" && new Date(exp.createdAt) > last24h
    );
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Exporter mes données</CardTitle>
            <CardDescription>
              Téléchargez une copie complète de vos données : conversations,
              certifications, profil utilisateur
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadExports}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  disabled={!canCreateNewExport()}
                  className="gap-2"
                >
                  <FileArchive className="h-4 w-4" />
                  Nouvel export
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer un export de données</DialogTitle>
                  <DialogDescription>
                    Sélectionnez le format et la période pour votre export de
                    données. Le traitement peut prendre quelques minutes selon
                    la quantité de données.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="format">Format</Label>
                    <Select
                      value={newFormat}
                      onValueChange={(value: "ZIP" | "JSON") =>
                        setNewFormat(value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ZIP">
                          ZIP - Archive complète avec fichiers
                        </SelectItem>
                        <SelectItem value="JSON">
                          JSON - Format structuré
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period">Période</Label>
                    <Select
                      value={newPeriod}
                      onValueChange={(
                        value: "ALL" | "LAST_YEAR" | "LAST_MONTH"
                      ) => setNewPeriod(value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une période" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">Toutes les données</SelectItem>
                        <SelectItem value="LAST_YEAR">
                          Dernière année
                        </SelectItem>
                        <SelectItem value="LAST_MONTH">Dernier mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button onClick={createExport} disabled={isCreating}>
                    {isCreating && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Créer l'export
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!canCreateNewExport() && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              <AlertCircle className="inline h-4 w-4 mr-1" />
              Vous pouvez créer un nouvel export dans 24h après votre dernière
              demande.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Chargement des exports...
            </div>
          ) : exports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileArchive className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun export trouvé</p>
              <p className="text-sm">Créez votre premier export de données</p>
            </div>
          ) : (
            exports.map((exportData) => (
              <div
                key={exportData.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge
                      variant={getStatusVariant(exportData.status)}
                      className="gap-1"
                    >
                      {getStatusIcon(exportData.status)}
                      {ExportService.getStatusLabel(exportData.status)}
                    </Badge>
                    <span className="text-sm font-medium">
                      {exportData.format} -{" "}
                      {ExportService.getPeriodLabel(exportData.period)}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div>
                      Créé le{" "}
                      {ExportService.formatExportDate(exportData.createdAt)}
                    </div>
                    {exportData.finishedAt && (
                      <div>
                        Terminé le{" "}
                        {ExportService.formatExportDate(exportData.finishedAt)}
                      </div>
                    )}
                    {exportData.fileSize && (
                      <div>
                        Taille :{" "}
                        {ExportService.formatFileSize(exportData.fileSize)}
                      </div>
                    )}
                    {exportData.error && (
                      <div className="text-red-600">
                        Erreur : {exportData.error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {exportData.status === "DONE" && (
                    <Button
                      size="sm"
                      onClick={() => downloadExport(exportData)}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
